<template>
    <div class="gch-container">
        <div class="gch-header">
            <div class="gch-logo-container">
                <img src="../feiraempreendedor/assets/logo-feira-empreendedor.svg" alt="Feira do Empreendedor 26" class="gch-logo" />
            </div>
        </div>

        <div v-if="atendenteRegistrado" class="gch-atendente-info">
            <div class="gch-atendente-info-dados">
                <div>
                    <span class="gch-atendente-info-label">Espaço:</span>
                    <strong>{{ espacoSelecionado?.nome }}</strong>
                </div>
                <div>
                    <span class="gch-atendente-info-label">Atendente:</span>
                    <strong>{{ atendenteLogin }}</strong>
                </div>
                <div>
                    <span class="gch-atendente-info-label">Mesa:</span>
                    <strong>{{ guicheNumero }}</strong>
                </div>
                <span v-if="mesaEhEspontanea" class="gch-badge-espontanea">Atendimento Espontâneo</span>
            </div>
            <Button label="Sair" icon="pi pi-sign-out" class="gch-btn" @click="sair" />
        </div>

        <!-- Layout em duas colunas quando o atendente esta logado:
             principal a esquerda (chamada atual) + fila lateral a direita. -->
        <div class="gch-atendente-grid" :class="{ 'gch-atendente-grid--solo': !atendenteRegistrado }">
            <div class="gch-card gch-atendente-card-principal">
                <div class="gch-atendente-body">
                    <div v-if="atendenteRegistrado" class="gch-fila-indicador" :class="{ 'gch-fila-indicador--ativo': temFila }">
                        <span class="gch-fila-indicador-dot" :class="{ 'gch-fila-indicador-dot--ativo': temFila }"></span>
                        <span v-if="proximosCarregando && filaCount === null" class="gch-fila-indicador-texto">Verificando fila…</span>
                        <span v-else-if="temFila" class="gch-fila-indicador-texto">
                            <strong>{{ filaCount }}</strong> {{ filaCount === 1 ? 'pessoa aguardando' : 'pessoas aguardando' }}
                        </span>
                        <span v-else class="gch-fila-indicador-texto">Sem ninguém na fila no momento</span>
                    </div>

                    <div class="gch-atendimento-atual">
                        <div v-if="chamadaAtual">
                            <div class="gch-atendimento-atual-senha">{{ chamadaAtual.senha }}</div>
                            <div class="gch-atendimento-atual-nome">{{ chamadaAtual.nomeAnonimizado || chamadaAtual.nomeCliente }}</div>
                            <div style="margin-top: 0.6rem; opacity: 0.9">{{ chamadaAtual.nomeEspaco }}</div>
                        </div>
                        <div v-else style="font-size: 1.6rem">Nenhum atendimento em andamento</div>
                    </div>

                    <div class="gch-atendimento-actions">
                        <Button
                            label="Chamar Próximo"
                            icon="pi pi-megaphone"
                            class="gch-btn gch-btn-primary"
                            :class="{ 'gch-btn-pulsar': temFila && !processando }"
                            :disabled="!atendenteRegistrado || processando"
                            @click="chamarProximo"
                        />
                    </div>
                </div>
            </div>

            <!-- Lista lateral: proximos atendimentos da mesa do consultor, ordenados
                 dos mais proximos (no tempo) ate os mais distantes. -->
            <aside v-if="atendenteRegistrado" class="gch-fila-lateral">
                <div class="gch-fila-lateral-titulo">Próximos atendimentos</div>
                <div v-if="proximosCarregando && proximos.length === 0" class="gch-fila-lateral-estado">Carregando…</div>
                <div v-else-if="proximos.length === 0" class="gch-fila-lateral-estado">Sem agendamentos para sua mesa.</div>
                <ol v-else class="gch-fila-lateral-lista">
                    <li
                        v-for="item in proximos"
                        :key="item.idAtendimento"
                        class="gch-fila-lateral-item"
                        :class="{
                            'gch-fila-lateral-item--em-atendimento': item.status === 'Chamado',
                        }"
                    >
                        <div class="gch-fila-lateral-horario">
                            <template v-if="mesaEhEspontanea">Por ordem</template>
                            <template v-else>{{ item.dataHoraAgendada ? formatarHorario(item.dataHoraAgendada) : 'Sem horário' }}</template>
                        </div>
                        <div class="gch-fila-lateral-info">
                            <div class="gch-fila-lateral-senha">{{ item.senha }}</div>
                            <div class="gch-fila-lateral-nome">{{ item.nomeAnonimizado || '—' }}</div>
                        </div>
                        <span class="gch-fila-lateral-status" :class="`gch-fila-lateral-status--${(item.status || '').toLowerCase()}`">
                            {{ rotuloStatus(item.status) }}
                        </span>
                    </li>
                </ol>
            </aside>
        </div>

        <Dialog
            v-model:visible="modalAtendenteVisivel"
            header="Identificação do atendente"
            modal
            :closable="false"
            :close-on-escape="false"
            :dismissable-mask="false"
            :style="{ width: '480px' }"
            class="gch-modal"
        >
            <div class="gch-modal-form">
                <div class="gch-modal-field">
                    <label class="gch-label">Espaço</label>
                    <Dropdown
                        v-model="formAtendente.espaco"
                        :options="espacos"
                        optionLabel="nome"
                        placeholder="Selecione o espaço"
                        class="gch-input"
                    />
                </div>
                <div class="gch-modal-field">
                    <label class="gch-label">Mesa</label>
                    <!-- Dropdown carregado a partir das mesas cadastradas no espaco
                         selecionado: o consultor reconhece a propria mesa (com nome do
                         responsavel) em vez de digitar livremente um numero. O nome do
                         atendente sai automaticamente do "consultor" da mesa. -->
                    <Dropdown
                        v-model="formAtendente.mesa"
                        :options="mesasDoEspaco"
                        optionLabel="rotulo"
                        :placeholder="
                            !formAtendente.espaco
                                ? 'Selecione o espaço antes'
                                : carregandoMesasModal
                                  ? 'Carregando mesas…'
                                  : mesasDoEspaco.length === 0
                                    ? 'Sem mesas para esse espaço'
                                    : 'Selecione sua mesa'
                        "
                        :disabled="!formAtendente.espaco || carregandoMesasModal || mesasDoEspaco.length === 0"
                        class="gch-input"
                    />
                    <small v-if="formAtendente.mesa?.consultor" class="gch-modal-hint">
                        Atendente: <strong>{{ formAtendente.mesa.consultor }}</strong>
                    </small>
                </div>
            </div>
            <template #footer>
                <Button
                    label="Salvar"
                    icon="pi pi-save"
                    class="gch-btn gch-btn-primary"
                    :disabled="!podeRegistrar || salvandoAtendente"
                    @click="registrarAtendente"
                />
            </template>
        </Dialog>
    </div>
</template>

<script>
import services from './services';

export default {
    name: 'GuicheAtendente',

    data() {
        return {
            espacos: [],
            espacoSelecionado: null,
            atendenteLogin: '',
            guicheNumero: '',
            mesaSelecionada: null,
            idAtendente: null,
            chamadaAtual: null,
            processando: false,
            salvandoAtendente: false,
            modalAtendenteVisivel: false,
            formAtendente: {
                espaco: null,
                mesa: null, // objeto Mesa (id, numero, consultor) — o "login" do atendente
                            // sai automaticamente do consultor da mesa, sem digitacao.
            },
            pollingIntervalId: null,
            INTERVALO_POLLING_MS: 3000,

            // Mesas disponiveis no modal de identificacao, carregadas com base no
            // espaco selecionado pelo atendente.
            mesasDoEspaco: [],
            carregandoMesasModal: false,

            // Lista lateral dos proximos atendimentos para a mesa do consultor.
            proximos: [],
            proximosCarregando: false,
        };
    },

    watch: {
        // Ao trocar de espaco no modal, recarrega a lista de mesas correspondente
        // e zera a selecao atual para evitar mesa de outro espaco.
        'formAtendente.espaco'(novo) {
            this.formAtendente.mesa = null;
            if (novo?.codigo) {
                this.carregarMesasDoEspaco(novo.codigo);
            } else {
                this.mesasDoEspaco = [];
            }
        },
    },

    computed: {
        atendenteRegistrado() {
            return !!this.idAtendente;
        },
        podeRegistrar() {
            // Mesa selecionada implica consultor associado (ou fallback "Mesa NN"),
            // entao basta espaco + mesa para identificar o atendente.
            return !!this.formAtendente.espaco && !!this.formAtendente.mesa?.numero;
        },
        filaCount() {
            if (!this.idAtendente) return null;
            if (this.proximosCarregando && this.proximos.length === 0) return null;
            return this.proximos.filter((p) => (p.status || '').toLowerCase() === 'aguardando').length;
        },
        temFila() {
            return typeof this.filaCount === 'number' && this.filaCount > 0;
        },
        mesaEhEspontanea() {
            return this.mesaSelecionada?.tipo === 'Espontanea';
        },
    },

    async mounted() {
        await this.carregarEspacos();
        if (!this.atendenteRegistrado) {
            this.abrirModalAtendente();
        }
        document.addEventListener('visibilitychange', this.aoMudarVisibilidade);
    },

    beforeUnmount() {
        this.pararPollingFila();
        document.removeEventListener('visibilitychange', this.aoMudarVisibilidade);
    },

    methods: {
        async carregarEspacos() {
            this.$store.dispatch('addRequest');
            try {
                // apenasComMesas=true: so faz sentido o consultor selecionar espacos
                // que tem mesas cadastradas — espacos legados sem fluxo ficam de fora.
                const resp = await services.obterEspacos({ autenticado: false, apenasComMesas: true });
                if (resp?.success) {
                    this.espacos = resp.data || [];
                }
            } finally {
                this.$store.dispatch('removeRequest');
            }
        },

        async carregarMesasDoEspaco(codigoEspaco) {
            if (!codigoEspaco) {
                this.mesasDoEspaco = [];
                return;
            }
            this.carregandoMesasModal = true;
            try {
                const resp = await services.obterMesasPorEspaco(codigoEspaco, { autenticado: false });
                if (resp?.success) {
                    // Rotulo no formato "Mesa NN — Consultor" para facilitar a
                    // identificacao na hora de o consultor escolher.
                    this.mesasDoEspaco = (resp.data || []).map((m) => ({
                        ...m,
                        rotulo: m.consultor ? `Mesa ${m.numero} — ${m.consultor}` : `Mesa ${m.numero}`,
                    }));
                } else {
                    this.mesasDoEspaco = [];
                    this.$toast?.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: (resp?.errors || ['Erro ao carregar mesas.'])[0],
                    });
                }
            } finally {
                this.carregandoMesasModal = false;
            }
        },

        abrirModalAtendente() {
            const espacoAtual = this.espacoSelecionado || null;
            // Se ja tinhamos um espaco e uma mesa selecionados (ex.: reentrada apos "Sair"),
            // tentamos reaproveitar a selecao depois que as mesas forem carregadas.
            this.formAtendente = {
                espaco: espacoAtual,
                mesa: null,
            };
            if (espacoAtual?.codigo) {
                this.carregarMesasDoEspaco(espacoAtual.codigo).then(() => {
                    if (this.guicheNumero) {
                        const previa = this.mesasDoEspaco.find(
                            (m) => String(m.numero).toLowerCase() === String(this.guicheNumero).toLowerCase(),
                        );
                        if (previa) this.formAtendente.mesa = previa;
                    }
                });
            } else {
                this.mesasDoEspaco = [];
            }
            this.modalAtendenteVisivel = true;
        },

        sair() {
            this.pararPollingFila();
            this.idAtendente = null;
            this.chamadaAtual = null;
            this.espacoSelecionado = null;
            this.atendenteLogin = '';
            this.guicheNumero = '';
            this.mesaSelecionada = null;
            this.proximos = [];
            this.mesasDoEspaco = [];
            this.abrirModalAtendente();
        },

        async atualizarFila() {
            await this.atualizarProximos();
        },

        async atualizarProximos() {
            if (!this.idAtendente) return;
            this.proximosCarregando = true;
            try {
                const resp = await services.obterFilaPorAtendente(this.idAtendente, { incluirChamado: true });
                if (resp?.success) {
                    this.proximos = Array.isArray(resp.data) ? resp.data : [];
                }
            } finally {
                this.proximosCarregando = false;
            }
        },

        iniciarPollingFila() {
            this.pararPollingFila();
            this.atualizarFila();
            this.pollingIntervalId = setInterval(this.atualizarFila, this.INTERVALO_POLLING_MS);
        },

        pararPollingFila() {
            if (this.pollingIntervalId) {
                clearInterval(this.pollingIntervalId);
                this.pollingIntervalId = null;
            }
        },

        aoMudarVisibilidade() {
            if (!this.atendenteRegistrado) return;
            if (document.hidden) {
                this.pararPollingFila();
            } else {
                this.iniciarPollingFila();
            }
        },

        async registrarAtendente() {
            if (!this.podeRegistrar) return;
            this.salvandoAtendente = true;
            this.$store.dispatch('addRequest');
            try {
                const espaco = this.formAtendente.espaco;
                const mesaObj = this.formAtendente.mesa;
                const numeroMesa = String(mesaObj?.numero || '').trim();
                // Login do atendente sai automaticamente do consultor da mesa; se a
                // mesa nao tiver consultor cadastrado (ex.: espontaneos), usamos
                // "Mesa NN" como identificador para o painel/historico.
                const login = (mesaObj?.consultor && mesaObj.consultor.trim()) || `Mesa ${numeroMesa}`;
                const resp = await services.registrarAtendente({
                    idEspaco: espaco.id,
                    login,
                    mesa: numeroMesa,
                });
                if (resp?.success && resp.data?.idAtendente) {
                    this.idAtendente = resp.data.idAtendente;
                    this.espacoSelecionado = espaco;
                    this.atendenteLogin = login;
                    this.guicheNumero = numeroMesa;
                    this.mesaSelecionada = mesaObj;
                    this.modalAtendenteVisivel = false;
                    this.$toast?.add({ severity: 'success', summary: 'Atendente registrado' });
                    this.iniciarPollingFila();
                } else {
                    this.$toast?.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: (resp?.errors || ['Erro ao registrar atendente'])[0],
                    });
                }
            } finally {
                this.salvandoAtendente = false;
                this.$store.dispatch('removeRequest');
            }
        },

        async chamarProximo() {
            if (!this.atendenteRegistrado) return;
            this.processando = true;
            this.$store.dispatch('addRequest');
            try {
                // O backend ja finaliza o atendimento atual (Status=Atendido) e move
                // o proximo para Status=Chamado em uma unica operacao, garantindo
                // consistencia mesmo em corridas com outro atendente.
                const resp = await services.chamarProximo({ idAtendente: this.idAtendente });
                if (resp?.success) {
                    if (resp.status === 204 || !resp.data) {
                        this.$toast?.add({ severity: 'info', summary: 'Fila vazia', detail: 'Não há próximos na fila.' });
                        this.chamadaAtual = null;
                    } else {
                        this.chamadaAtual = resp.data;
                        this.$toast?.add({ severity: 'success', summary: 'Chamado', detail: `Senha ${resp.data.senha}` });
                    }
                    this.atualizarFila();
                } else {
                    this.$toast?.add({ severity: 'error', summary: 'Erro', detail: (resp?.errors || ['Erro'])[0] });
                }
            } finally {
                this.processando = false;
                this.$store.dispatch('removeRequest');
            }
        },

        formatarHorario(dataIso) {
            if (!dataIso) return '';
            try {
                const d = new Date(dataIso);
                if (isNaN(d.getTime())) return '';
                const hh = String(d.getHours()).padStart(2, '0');
                const mm = String(d.getMinutes()).padStart(2, '0');
                return `${hh}:${mm}`;
            } catch (e) {
                return '';
            }
        },

        rotuloStatus(status) {
            switch ((status || '').toLowerCase()) {
                case 'chamado':
                    return 'Em atendimento';
                case 'aguardando':
                    return 'Aguardando';
                case 'atendido':
                    return 'Atendido';
                default:
                    return status || '';
            }
        },

        onSenhaGerada() {},
    },
};
</script>

<style src="./guiche.css"></style>
